// src/components/SupportRequest.js - Support request form component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function SupportRequest() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Demo submission - will be replaced with API call later
      console.log('Support request submitted:', formData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess(true);
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOkClick = () => {
    navigate('/dashboard');
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6 font-sans text-gray-800 relative">
        <div className="flex justify-center mt-10">
          <img 
            src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/CompassLanguages_Logo4_PNG.png" 
            alt="Compass Languages Logo" 
            className="h-20 object-contain" 
          />
        </div>
        <div className="text-center">
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-8 rounded-lg shadow-xl">
            <h2 className="text-2xl font-bold mb-4">Support Request Sent Successfully!</h2>
            <p className="text-lg mb-6">Your message has been received. Our support team will get back to you soon.</p>
            <button 
              onClick={handleOkClick}
              className="bg-primary hover:bg-blue-600 text-white font-semibold px-6 py-2 rounded shadow"
            >
              OK
            </button>
          </div>
        </div>
        
        {/* Footer Section */}
        <div className="pt-8 text-center text-sm text-gray-600">
          Modular Customized Localization Platform by Compass Languages
          <div className="mt-2 flex justify-center">
            <img 
              src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/CompassLanguages_Logo4_PNG.png" 
              className="h-20" 
              alt="Compass Logo" 
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 font-sans text-gray-800 relative">
      {/* Top right buttons */}
      <div className="absolute right-6 top-6 flex gap-2">
        <button 
          onClick={() => navigate('/dashboard')}
          className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded shadow"
        >
          Back to Dashboard
        </button>
      </div>
      
      <h1 className="text-3xl font-bold text-primary text-center">Support Request</h1>

      <div className="shadow-xl border border-primary rounded-lg p-6 space-y-4 bg-white">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold">
                Name <span className="text-red-500">*</span>
              </label>
              <input 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded focus:ring-2 focus:ring-primary" 
                placeholder=""
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input 
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border border-blue-300 p-2 rounded focus:ring-2 focus:ring-primary" 
                placeholder=""
                required
              />
            </div>
          </div>

          {/* Question field */}
          <div>
            <label className="block text-gray-700 font-semibold">
              Please ask any questions <span className="text-red-500">*</span>
            </label>
            <textarea 
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              className="w-full border border-blue-300 p-2 rounded focus:ring-2 focus:ring-primary" 
              placeholder="Please describe your question or issue in detail..."
              rows="6"
              required
            />
          </div>

          <div className="text-right space-x-2">
            <button 
              type="button"
              onClick={() => navigate('/dashboard')}
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      {/* Footer Section */}
      <div className="pt-8 text-center text-sm text-gray-600">
        Modular Customized Localization Platform by Compass Languages
        <div className="mt-2 flex justify-center">
          <img 
            src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/CompassLanguages_Logo4_PNG.png" 
            className="h-20" 
            alt="Compass Logo" 
          />
        </div>
      </div>
    </div>
  );
}

export default SupportRequest;